// schema
import * as ISchema from "@schema";

// libraries
import * as React from "react";
import { RelayProp } from "react-relay";
import { QueryRenderer, ReadyState } from "react-relay";
import { Match } from "found";

// state and mutations
import { asanaTaskEventQuery } from "./AsanaTask.gql";
import { getOnboardingFlowState } from "@state/onboarding";

// actions
import { getReduxStore } from "../../state/redux-store";
import { setPartnerLogo } from "@actions/onboarding.actions";

// components
import { OnboardingNUX } from "@components/onboarding-nux";
import { LoaderClock } from "@clockwise/web-commons/src/components/loader-clock";
import { NavBar } from "@clockwise/web-commons/src/ui/nav-bar";
import { Aclacka } from "@clockwise/web-commons/src/ui/aclacka";
import { asanaTask as asanaTaskSvg } from "@img/integration-logos";
import { EventMissing } from "../event/EventMissing";

// material-ui
import { Button } from "@material-ui/core";

// util
import { getCurrentOrg } from "@util/org.util";
import { logger } from "@src/util/logger.util";
import { asanaTrack as track, TrackingEvents } from "@util/analytics.util";
import { generateGcalEventLink } from "@clockwise/web-commons/src/util/url.util";
import { eventFromParent } from "@clockwise/client-commons/src/util/event";
import { url, paths } from "@clockwise/client-commons/src/constants/site";

// material-ui
import { makeStyles } from "@material-ui/core";
import { greys } from "@clockwise/web-commons/src/styles/color.styles";
import { getValue } from "@clockwise/web-commons/src/util/errorable.util";

type IAsanaTaskStep = "" | "onboarding";

export interface AsanaTaskProps {
  viewer: ISchema.IViewer;
  relay: RelayProp;
  match: Match;
}

const useStyles = makeStyles({
  container: {},
  content: {
    marginTop: 155,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexWrap: "wrap",
  },
  rightContent: {
    marginBottom: 29,
  },
  aclacka: {
    paddingLeft: 14,
  },
  asanaTaskSvg: {
    width: 350,
    margin: "0px 50px 40px 18px",
    "@media (max-width: 829px)": {
      marginLeft: 40,
    },
  },
  buttonsContainer: {
    display: "flex",
    flexDirection: "row",
    "@media (max-width: 829px)": {
      justifyContent: "center",
    },
  },
  text: {
    padding: "18px 14px 14px",
    color: greys.slate,
    lineHeight: 1.4,
    fontSize: 18,
    maxWidth: 480,
  },
  learnMore: { width: 165, padding: 12, margin: 12 },
  backToAsana: { paddingRight: 20, width: 165, padding: 12, margin: 12 },
});

export const AsanaTask = ({ viewer, relay, match }: AsanaTaskProps) => {
  const classes = useStyles();

  const [step, setStep] = React.useState<IAsanaTaskStep>("");

  const org = getCurrentOrg(viewer);
  const onboardingState = viewer && getOnboardingFlowState(viewer.user);

  React.useEffect(() => {
    if (!viewer) {
      return;
    }

    if (onboardingState?.current?.state !== "Finished") {
      setStep("onboarding");
      track(TrackingEvents.ASANA.STARTED_ONBOARDING_FROM_ASANA_TASK);
      return;
    } else {
      if (step === "onboarding") {
        track(TrackingEvents.ASANA.FINISHED_ONBOARDING_FROM_ASANA_TASK);
      }
    }
  }, [viewer]);

  if (!onboardingState || !viewer || !org) {
    return null;
  }

  if (onboardingState.current.state !== "Finished") {
    const store = getReduxStore();
    store.dispatch(setPartnerLogo("asana"));

    return (
      <div className={classes.container}>
        <div>
          <OnboardingNUX
            onboardingState={onboardingState}
            environment={relay.environment}
            onRequestClose={() => null}
            org={org}
            mode="fixed"
          />
        </div>
      </div>
    );
  }

  const calendarId = atob(match?.params?.["calendar_id"] || "");
  const externalEventId = atob(match?.params?.["event_id"] || "");

  if (calendarId && externalEventId && org.primaryOrgCalendar) {
    return (
      <QueryRenderer
        environment={relay.environment}
        query={asanaTaskEventQuery}
        variables={{ orgRelayId: org.id, externalEventId, calendarId }}
        render={(readyState: ReadyState) => {
          const { error, props } = readyState;
          if (error) {
            logger.error("Error fetching Asana Focus Task event details");
            return <FocusTaskNonOwnerPage calendarId={calendarId} />;
          }

          const org = (props && props.org) || null;

          if (!org) {
            return <LoaderClock />;
          }

          const eventParent = getValue(org?.forceFetchEventParentErrorable);
          const event = eventFromParent<ISchema.IEvent>(eventParent, calendarId);

          if (!event) {
            return <FocusTaskNoEventPage />;
          }

          if (event?.organizer?.urnValue === org.primaryOrgCalendar) {
            track(TrackingEvents.ASANA.VISITED_OWN_FOCUS_TASK);
            const calendarEventUrl = generateGcalEventLink(externalEventId, calendarId);
            window.location.href = calendarEventUrl;
            return null;
          } else {
            track(TrackingEvents.ASANA.VISITED_OTHERS_FOCUS_TASK);
          }

          return <FocusTaskNonOwnerPage calendarId={calendarId} />;
        }}
      />
    );
  } else {
    logger.error("Missing event_id to find Asana Focus Task");
  }

  return <FocusTaskNonOwnerPage calendarId={calendarId} />;
};

const FocusTaskNoEventPage = () => {
  const classes = useStyles();

  return (
    <div>
      <NavBar />
      <div className={classes.content}>
        <EventMissing hideYourCalendar={true} />
      </div>
    </div>
  );
};

const FocusTaskNonOwnerPage = ({ calendarId }: { calendarId: string }) => {
  const classes = useStyles();

  return (
    <div>
      <NavBar />
      <div className={classes.content}>
        <img className={classes.asanaTaskSvg} src={asanaTaskSvg} alt="Asana Task" />
        <div className={classes.rightContent}>
          <Aclacka className={classes.aclacka} />
          <div className={classes.text}>
            {calendarId ? (
              <span>{calendarId} is using Clockwise to add Asana tasks to their calendar</span>
            ) : (
              <span>
                Use Clockwise to add
                <br />
                Asana tasks to your calendar
              </span>
            )}
          </div>
          <div className={classes.buttonsContainer}>
            <Button
              href={`${url}${paths.asanaLanding}`}
              onClick={() => {
                track(TrackingEvents.ASANA.VISITED_LEARN_MORE_FROM_ASANA_TASK);
              }}
              variant="contained"
              color="primary"
              className={classes.learnMore}
            >
              Learn More
            </Button>
            <Button className={classes.backToAsana} color="primary" href="https://app.asana.com">
              Back to Asana
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
